import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useContent, useContentLoaded } from "../content/ContentProvider";
import { RouteFadeContext } from "../App";
import FilmBackdrop from "../components/FilmBackdrop";
import StickyCta from "../components/StickyCta";
import { layoutWorkDetail } from "../lib/workDetailLayout";
import { ArrowLeft } from "lucide-react";

/**
 * Project detail page — same template per work.
 * Tall scrollable page over the shared FilmBackdrop. Five overlapping
 * landscape stills are arranged vertically with only slight corner overlaps.
 * Halfway down, a description block fades in as it scrolls into view.
 */
export default function WorkDetail() {
  const { slug } = useParams();
  const { fadeTo } = useContext(RouteFadeContext);
  const { works: WORKS, worksDeeper } = useContent();
  const loaded = useContentLoaded();
  const WORKS_DEEPER_BACKGROUND = worksDeeper.background;

  const galleryBottomMargin = worksDeeper.galleryBottomMargin ?? 0.5;
  const work = WORKS.find((w) => w.slug === slug);
  // Defer the redirect until /api/content has settled. Without this, a
  // direct hit on /works/deeper/<slug> renders against the cold-start
  // defaultContent (works: [] post-fork), can't find the work, and bounces
  // to /works/deeper before the live KV doc arrives — even though the slug
  // is valid. Once loaded, treat a missing or hidden work as invalid.
  const isInvalid = loaded && (!work || work.enabled === false);

  // Safe fallbacks keep derived values well-typed even when work is absent,
  // so the hooks below can run unconditionally (Rules of Hooks).
  const dp = (work && work.descriptionPosition) || {};
  // When the user has set a full position via the LayoutModal, x is the
  // *left edge* (matches the wireframe semantic). When unset, fall back to
  // the original centered-via-translate behavior.
  const hasFullOverride =
    typeof dp.x === "number" && typeof dp.y === "number" && typeof dp.w === "number";
  const descX = typeof dp.x === "number" ? dp.x : 50;
  const descW = typeof dp.w === "number" ? dp.w : null;

  const galleryItems = (work && work.gallery) || [];

  // Measured naturalHeight/naturalWidth per gallery image, filled progressively
  // as each image fires onLoad. Drives non-overlapping layout positions.
  const [imageRatios, setImageRatios] = useState([]);
  const onGalleryImgLoad = (e, i) => {
    const img = e.currentTarget;
    if (!img.naturalWidth) return;
    const ratio = img.naturalHeight / img.naturalWidth;
    setImageRatios((prev) => {
      const next = [...prev];
      next[i] = ratio;
      return next;
    });
  };

  // Viewport dimensions — updated on resize for accurate layout and margin conversion.
  const [vwToVh, setVwToVh] = useState(() => window.innerWidth / window.innerHeight);
  const [innerHeight, setInnerHeight] = useState(() => window.innerHeight);
  useEffect(() => {
    const onResize = () => {
      setVwToVh(window.innerWidth / window.innerHeight);
      setInnerHeight(window.innerHeight);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // When no explicit descriptionPosition.y is saved, auto-place the description
  // below the last image rather than defaulting to a hardcoded 112vh which can
  // overlap images on wide viewports.
  const POSITIONS = useMemo(
    () => layoutWorkDetail(galleryItems.length, undefined, imageRatios, vwToVh),
    [galleryItems.length, imageRatios, vwToVh],
  );
  const descY = useMemo(() => {
    if (typeof dp.y === "number") return dp.y;
    const bottom = POSITIONS.reduce((max, pos, i) => {
      const ratio = typeof imageRatios[i] === "number" ? imageRatios[i] : 1.5;
      return Math.max(max, pos.y + pos.w * vwToVh * ratio);
    }, 30);
    return Math.ceil(bottom + 12);
  }, [dp.y, POSITIONS, imageRatios, vwToVh]);

  // marginVh: convert configured inches to vh using actual viewport height.
  // 1 CSS in = 96px always.
  const marginVh = (galleryBottomMargin * 96) / (innerHeight / 100);

  // Compute page height from ACTUAL rendered positions (saved g.y/g.w overrides
  // take priority over auto-layout). workDetailPageHeightVh only uses auto
  // positions, so it misses manually repositioned images and leaves no margin.
  const PAGE_HEIGHT = useMemo(() => {
    const bottom = galleryItems.reduce((max, g, i) => {
      const auto = POSITIONS[i] || { y: 8, w: 44 };
      const y = typeof g.y === "number" ? g.y : auto.y;
      const w = typeof g.w === "number" ? g.w : auto.w;
      const ratio = typeof imageRatios[i] === "number" ? imageRatios[i] : 1.5;
      return Math.max(max, y + w * vwToVh * ratio);
    }, Math.max(descY + 20, 30));
    return Math.max(bottom, 60); // minimum sensible page height
  }, [galleryItems, POSITIONS, imageRatios, vwToVh, descY]);

  // All hooks called unconditionally — Navigate redirect comes after.
  const workTitle = work ? work.title : "";
  useEffect(() => {
    if (!isInvalid) document.title = `${workTitle} · California Sexy`;
  }, [workTitle, isInvalid]);

  const descRef = useRef(null);
  const [descVisible, setDescVisible] = useState(false);

  useEffect(() => {
    // reset scroll, description visibility, and measured ratios on slug change
    window.scrollTo({ top: 0, behavior: "auto" });
    setDescVisible(false);
    setImageRatios([]);
  }, [slug]);

  useEffect(() => {
    const el = descRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.25) {
            setDescVisible(true);
          }
        });
      },
      { threshold: [0.0, 0.25, 0.5, 0.75, 1.0] }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const goBack = (e) => {
    e.preventDefault();
    fadeTo("/works/deeper", 600);
  };

  // Redirect after all hooks — disabled or unknown works go back to the gallery.
  if (isInvalid) return <Navigate to="/works/deeper" replace />;
  // Render a black placeholder while content is still loading and the slug
  // hasn't resolved yet. Avoids crashing on `work.X` references below and
  // gives a clean fade rather than a flash of stale defaultContent.
  if (!work) {
    return (
      <div
        style={{
          minHeight: "100vh",
          width: "100vw",
          backgroundColor: "#000",
        }}
      />
    );
  }

  return (
    <div
      className="page-enter"
      style={{
        position: "relative",
        minHeight: `${PAGE_HEIGHT + marginVh}vh`,
        width: "100vw",
        backgroundColor: "#000",
        backgroundImage: `url("${WORKS_DEEPER_BACKGROUND}")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center top",
        backgroundSize: "cover",
        backgroundAttachment: "fixed",
        overflowX: "hidden",
      }}
    >
      {worksDeeper.filmBackdrop && <FilmBackdrop />}

      {/* Back — fixed so it follows the scroll */}
      <a
        href="/works/deeper"
        onClick={goBack}
        className="font-math"
        style={{
          position: "fixed",
          top: "1.4rem",
          left: "1.6rem",
          color: "#f4ece0",
          textDecoration: "none",
          fontSize: "0.95rem",
          display: "inline-flex",
          alignItems: "center",
          gap: "0.5rem",
          opacity: 0.85,
          zIndex: 60,
        }}
      >
        <ArrowLeft size={16} strokeWidth={1.5} />
        back
      </a>

      {/* Title + role — fixed, top right */}
      <div
        className="font-math"
        style={{
          position: "fixed",
          top: "1.4rem",
          right: "1.6rem",
          color: "rgba(244,236,224,0.85)",
          fontSize: "0.85rem",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          zIndex: 60,
          textAlign: "right",
        }}
      >
        <div>
          <em style={{ fontStyle: "italic", letterSpacing: "0.04em" }}>
            {work.title}
          </em>
          <span style={{ opacity: 0.6 }}>
            {"  \u2014  "}
            {work.year}
          </span>
        </div>
        {work.role && (
          <div
            style={{
              marginTop: "0.45rem",
              fontSize: "0.72rem",
              letterSpacing: "0.14em",
              opacity: 0.65,
              textTransform: "uppercase",
              fontStyle: "italic",
            }}
          >
            {work.role}
          </div>
        )}
      </div>

      {/* Overlapping landscape stills — straight, no shadow, slight corner overlap */}
      {galleryItems.map((g, i) => {
        const auto = POSITIONS[i] || { x: 8, y: 8, w: 44 };
        const x = typeof g.x === "number" ? g.x : auto.x;
        const y = typeof g.y === "number" ? g.y : auto.y;
        const w = typeof g.w === "number" ? g.w : auto.w;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${x}%`,
              top: `${y}vh`,
              width: `${w}vw`,
              overflow: "hidden",
              zIndex: 10 + i,
            }}
          >
            <img
              src={g.url}
              alt={`${work.title} — still ${i + 1}`}
              loading={i === 0 ? "eager" : "lazy"}
              decoding="async"
              onLoad={(e) => onGalleryImgLoad(e, i)}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                filter: "contrast(1.05) saturate(0.95) brightness(0.97)",
              }}
            />
          </div>
        );
      })}

      {/* Description — fades in when it scrolls into view (halfway down the page) */}
      <div
        ref={descRef}
        className="font-math"
        style={{
          position: "absolute",
          left: `${descX}%`,
          top: `${descY}vh`,
          width: descW ? `${descW}vw` : "min(58ch, 80vw)",
          color: "#f4ece0",
          textAlign: "center",
          opacity: descVisible ? 1 : 0,
          transform: hasFullOverride
            ? (descVisible ? "translate(0, 0)" : "translate(0, 18px)")
            : (descVisible ? "translate(-50%, 0)" : "translate(-50%, 18px)"),
          transition: "opacity 1200ms ease, transform 1200ms ease",
          zIndex: 30,
        }}
      >
        <p
          style={{
            margin: 0,
            fontStyle: "italic",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            fontSize: "0.72rem",
            opacity: 0.65,
          }}
        >
          {work.role}
        </p>
        <p
          style={{
            marginTop: "1.2rem",
            marginBottom: 0,
            fontStyle: "italic",
            fontSize: "clamp(1.05rem, 1.4vw, 1.35rem)",
            lineHeight: 1.6,
            color: "rgba(244,236,224,0.92)",
          }}
        >
          {work.description}
        </p>
      </div>

      {work.externalLink && (
        <StickyCta
          label={work.externalLink.label}
          target={work.externalLink.url}
          mode="fixed"
          external
        />
      )}
    </div>
  );
}
